import { TransportError } from '../../core/transport/errors';
import { ParseError } from '../../core/bytes/errors';
import { href, withFrom } from '../../router';
import { Icon } from './Icon';
import { isDesktop } from '../state/desktop';
import { settings, updateSettings } from '../state/settings';
import { proxyOffer } from '../state/transport';

export function ErrorBox({ error, title }: { error: unknown; title?: string }) {
  let heading = title ?? 'Something went wrong';
  let message = error instanceof Error ? error.message : String(error);
  let hint: string | undefined;
  let blocked = false;
  if (error instanceof TransportError) {
    heading = title ?? { 'cors-blocked': 'Blocked by the browser (CORS)', dns: 'Host unreachable', 'rate-limited': 'Rate limited', network: 'Network error', http: 'The log returned an error', parse: 'Unreadable response', aborted: 'Cancelled' }[error.kind];
    hint = error.hint;
    blocked = error.kind === 'cors-blocked' && !isDesktop();
    if (error.kind === 'http') message = error.detail ? `HTTP ${error.status}: ${error.detail}` : `HTTP ${error.status} for ${error.url}`;
  } else if (error instanceof ParseError) {
    heading = title ?? 'Could not parse the log data';
  }
  return (
    <div class="errorbox" role="alert">
      <strong>{heading}</strong>
      <div class="mono small wrap">{message}</div>
      {blocked ? <ProxyRemedy /> : hint && <div class="small">{hint}</div>}
    </div>
  );
}

/**
 * What to do about a log that blocks browsers. Undecided users on merklens.app are offered the
 * built-in proxy right here (saying yes re-runs the blocked request); everyone else gets the way to
 * Settings. The proxy is only ever used for requests the browser itself could not make.
 */
function ProxyRemedy() {
  const mode = settings.value.proxy;
  const to = withFrom(href('/settings'));
  if (proxyOffer.value) {
    return (
      <div class="small stack" style="gap:0.4rem;margin-top:0.2rem">
        <div>
          This log does not allow browser requests (no CORS headers). Merkle Lens runs a proxy that forwards only to known CT logs; it would see which log URLs you fetch and nothing else, and every response is still verified in your browser. Use it for logs that block the browser?
        </div>
        <div class="row">
          <button type="button" class="btn btn-sm" onClick={() => updateSettings({ proxy: 'builtin' })}><Icon name="check" /> Use the built-in proxy</button>
          <button type="button" class="btn btn-sm btn-ghost" onClick={() => updateSettings({ proxy: 'off' })}>Not now</button>
          <a class="small" href={to}>Use my own proxy…</a>
        </div>
      </div>
    );
  }
  const text =
    mode === 'off'
      ? 'This log does not allow browser requests (no CORS headers) and the proxy is turned off.'
      : mode === 'ask'
        ? 'This log does not allow browser requests (no CORS headers). Read it through a CORS proxy or use the desktop app.'
        : 'This log does not allow browser requests (no CORS headers), and the request failed through the proxy as well.';
  return (
    <div class="small stack" style="gap:0.4rem;margin-top:0.2rem">
      <div>{text}</div>
      <div class="row"><a class="btn btn-sm" href={to}><Icon name="settings" /> Proxy settings</a></div>
    </div>
  );
}
